import { Callout, DirectionalHint, IProcessedStyleSet, mergeStyles, styled } from '@fluentui/react';
import * as React from 'react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { appContext } from '../../../../web/models/getHelp/appContext';
import { getSearchAutoSuggestCalloutStyle } from '../../../../web/self-help/styles/selfHelpStyles';
import { getHelpLogger } from '../../../../web/utils/getHelpLogger';
import { getRandomGuidString } from '../../../../web/utils/getHelputils';
import { capturePageAction, collectTelemetry } from '../../../../web/utils/oneDsUtil';
import { AutoSuggestResult, AutoSuggestResultV2, Modality } from '../../../models/apiModels';
import { AutomationIds } from '../../../models/constants/automationIds';
import { AutoSuggestConstants, AutoSuggestV2Constants } from '../../../models/constants/constants';
import { IAppState } from '../../../models/reduxState';
import { buildAutoSuggestContactUsFields, sanitizeHTML } from '../../../utilities';
import { setVsbUpsellAlertActivated } from '../../app/appActions';
import { getSuggestionsSelector, isGetHelpSelector } from '../../app/selectors/contactUsSelector';
import { hideAutoSuggest, setAutoSuggestResult, setAutoSuggestResultV2, setOriginalQuery } from '../reduxUpdater/actions/autoSuggestActions';
import { initializeModalityStateOnGetHelp } from '../reduxUpdater/actions/modalitySectionActions';
import { setContactSearchedQueryText } from '../reduxUpdater/actions/sharedStateActions';
import { dispatchAlchemyFetch } from '../reduxUpdater/dispatchers/contactUsDispatchers';
import { getAutoSuggestListclassNames, getAutoSuggestListStyles, IAutoSuggestSuggestListStyles } from '../styles/selfHelpStyles';

import { IStyledComponent } from '../styles/themesUI';
import { ActionType, LogHint, LogType } from '../../../../web/models/emeraldLoggingRequest';

interface AutoSuggestProps {
  inputBoxRef: any;
  hideRequestCreationTab: (isHidden: boolean) => void;
}

type ISelfHelpAutoSuggestProps = AutoSuggestProps & IStyledComponent<IAutoSuggestSuggestListStyles>;

const SelfHelpAutoSuggestC = (props: ISelfHelpAutoSuggestProps) => {
  const autoSuggestState = useSelector((state: IAppState) => state.ContactState.AutoSuggestState);
  const modalitySectionState = useSelector((state: IAppState) => state.ContactState.ModalitySectionState);

  const dispatch = useDispatch();
  const dispatchSetAutoSuggestResult = useCallback((autoSuggestResults: AutoSuggestResult[]) => dispatch(setAutoSuggestResult(autoSuggestResults)), [dispatch]);
  const dispatchSetAutoSuggestResultV2 = useCallback((autoSuggestResultsV2: AutoSuggestResultV2) => dispatch(setAutoSuggestResultV2(autoSuggestResultsV2)), [dispatch]);
  const dispatchHideAutoSuggest = useCallback(() => dispatch(hideAutoSuggest), [dispatch]);
  const dispatchInitializeModalityStateOnGetHelp = useCallback((modalities: Modality[]) => dispatch(initializeModalityStateOnGetHelp(modalities)), [dispatch]);
  const dispatchSetContactSearchedQueryText = useCallback((query: string, isAutoSuggestQuery: boolean) => dispatch(setContactSearchedQueryText(query, isAutoSuggestQuery)), [dispatch]);
  const dispatchSetOriginalQuery = useCallback((originalQuery: string) => dispatch(setOriginalQuery(originalQuery)), [dispatch]);
  const dispatchSetVsbUpsellAlertActivated = useCallback(() => dispatch(setVsbUpsellAlertActivated()), [dispatch]);
  const dispatchDispatchAlchemyFetch = useCallback((title: string) => dispatch(dispatchAlchemyFetch(title)), [dispatch]);

  const useCallout: boolean = useSelector(isGetHelpSelector);
  const suggestions: string[] | AutoSuggestResult[] = useSelector(getSuggestionsSelector);

  const shouldRender = !!autoSuggestState.isAutoSuggestV2Enabled
    ? !!autoSuggestState.autoSuggestResultsV2 && autoSuggestState.autoSuggestResultsV2.suggestions?.length > -1
    : !!autoSuggestState.autoSuggestResults && autoSuggestState.autoSuggestResults.length > -1;

  if (!shouldRender) {
    return null;
  }

  const onSuggestionSelect = useCallback((isClick: boolean, title: string, queryIndex: number) => {
    appContext.sessionId = getRandomGuidString();
    dispatchSetContactSearchedQueryText(title, true);
    dispatchSetAutoSuggestResult([]);
    dispatchSetAutoSuggestResultV2({ suggestions: [], searchText: [] });
    getHelpLogger.logOnSearch(true, true);
    collectTelemetry({
      ContentId: 'ClickedAutoSuggest', Type: LogType.PageAction, Hint: LogHint.ClickedAutoSuggest,
      ActionType: isClick ? ActionType.ClickLeft : ActionType.KeyboardEnter,
      Details: {
        'originalQueryLength': autoSuggestState.originalQuery?.length,
        'suggestionsLength': suggestions?.length,
        'queryIndex': queryIndex,
      }
    });
    dispatchDispatchAlchemyFetch(title);
    dispatchInitializeModalityStateOnGetHelp(null);
    getHelpLogger.logAutoSuggestionsClicked(title, suggestions?.length, queryIndex, autoSuggestState.originalQuery);
    const searchOverrideTags = {
      behavior: 160,
      content: {
        contentId: 'getHelp',
        areaName: 'formQuerySubmit',
        contentName: 'submitButton',
        formnm: 'maxFunnel',
        selfhelpprovider: 'alchemy',
        srchq: title.trim(),
        contactusfields: buildAutoSuggestContactUsFields(
          false,
          autoSuggestState.isAutoSuggestEnabled || autoSuggestState.isAutoSuggestV2Enabled,
          autoSuggestState.originalQuery,
          suggestions,
          true,
          queryIndex,
          modalitySectionState.alchemyThemeRoutingEnabled
        ),
      },
      actionType: isClick ? 'CL' : 'KE',
    };
    capturePageAction(null, searchOverrideTags);
    dispatchSetOriginalQuery(title);
    dispatchSetVsbUpsellAlertActivated();
    props.hideRequestCreationTab(false);
  }, []);

  const onKeyDown = useCallback((e: any, currentIndex: number, suggestionText: string) => {
    if (e.keyCode === 13) {
      // enter key
      onSuggestionSelect(false, suggestionText, currentIndex);
    } else if (e.keyCode === 27) {
      // esc key
      dispatchHideAutoSuggest();
      props.inputBoxRef.current.focus();
    }
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const getBoldAutoSuggestString = (autoSuggestString: string) => {
    const startIndex = !!autoSuggestString && !!autoSuggestState.originalQuery
      ? autoSuggestString.toLowerCase().indexOf(autoSuggestState.originalQuery.toLowerCase()) : -1;
    const endIndex = autoSuggestState.originalQuery.length + startIndex;

    if (startIndex === -1 || (startIndex === 0 && endIndex === autoSuggestString.length)) {
      return { __html: autoSuggestString };
    }

    const beginningString = autoSuggestString.substring(0, startIndex);
    const middleString = autoSuggestString.substring(startIndex, endIndex);
    const endString = autoSuggestString.substring(endIndex, autoSuggestString.length);
    return sanitizeHTML(`${beginningString}<b>${middleString}</b>${endString}`);
  };

  const renderAutoSuggest = (
    autoSuggestResults: AutoSuggestResult[],
    autoSuggestListClassNames: IProcessedStyleSet<IAutoSuggestSuggestListStyles>
  ) => {
    return (
      <ul className={mergeStyles(autoSuggestListClassNames.socSuggestionList, autoSuggestListClassNames.supSuggestionList)} data-automation-id={AutomationIds.AutoSuggestOptions}>
        {
          autoSuggestResults.map((result, index) =>
            <li
              key={result.title}
              data-key={`${AutoSuggestConstants.dataKeyName}${index}`}
              className={mergeStyles(autoSuggestListClassNames.supSuggestionItem,
                autoSuggestState.autoSuggestFocusedIndex === index
                  ? autoSuggestListClassNames.supSuggestionItemHighlight
                  : '')}
              tabIndex={-1}
              data-automation-id={`${AutomationIds.AutoSuggestOption}-${index}`}
              onMouseDown={() => {
                // use onMouseDown instead of onClick as a compatibility fix for InApp (IE 11)
                onSuggestionSelect(true, result.title, index);
              }}
              onKeyDown={e => onKeyDown(e, index, result.title)}
            >
              {/* eslint-disable-next-line react/no-danger */}
              <div dangerouslySetInnerHTML={getBoldAutoSuggestString(result['@search.text'])} />
            </li>
          )
        }
      </ul>
    );
  };

  const renderAutoSuggestV2 = (
    autoSuggestResultsV2: AutoSuggestResultV2,
    autoSuggestListClassNames: IProcessedStyleSet<IAutoSuggestSuggestListStyles>
  ) => {
    return (
      <ul className={mergeStyles(autoSuggestListClassNames.socSuggestionList, autoSuggestListClassNames.supSuggestionList)} data-automation-id={AutomationIds.AutoSuggestOptions}>
        {
          autoSuggestResultsV2.suggestions.map((result, index) =>
          <li
              key={result}
              aria-posinset={index+1}
              aria-setsize={autoSuggestResultsV2.suggestions.length}
              aria-label={autoSuggestResultsV2.suggestions[index]}
              data-automation-id={`${AutomationIds.AutoSuggestOption}-${index}`}
              data-key={`${AutoSuggestV2Constants.dataKeyName}${index}`}
              className={mergeStyles(autoSuggestListClassNames.supSuggestionItem,
                autoSuggestState.autoSuggestFocusedIndex === index
                  ? autoSuggestListClassNames.supSuggestionItemHighlight
                  : '')}
              tabIndex={-1}
              onMouseDown={() => {
                // use onMouseDown instead of onClick as a compatibility fix for InApp (IE 11)
                onSuggestionSelect(true, result, index);
              }}
              onKeyDown={e => onKeyDown(e, index, result)}
            >
              {/* eslint-disable-next-line react/no-danger */}
              <div dangerouslySetInnerHTML={sanitizeHTML(autoSuggestResultsV2.searchText[index])} />
            </li>
          )
        }
      </ul>
    );
  };

  const autoSuggestListClassNames = getAutoSuggestListclassNames(props.styles, { theme: props.theme });

  const autoSuggestContent = !!autoSuggestState.isAutoSuggestV2Enabled
    ? renderAutoSuggestV2(autoSuggestState.autoSuggestResultsV2, autoSuggestListClassNames)
    : renderAutoSuggest(autoSuggestState.autoSuggestResults, autoSuggestListClassNames);
    
  return useCallout ? (
    <Callout
      target={props.inputBoxRef?.current}
      directionalHint={DirectionalHint.bottomLeftEdge}
      directionalHintFixed={false}
      styles={getSearchAutoSuggestCalloutStyle(
        props.theme.palette.neutralLighterAlt,
        props.theme.palette.neutralLight
      )}
      calloutWidth={props.inputBoxRef?.current ? props.inputBoxRef.current.clientWidth : 0}
      gapSpace={0}
      isBeakVisible={false}
      beakWidth={0}
      doNotLayer={true}
      data-automation-id={AutomationIds.AutoSuggestCallout}
    >
      {autoSuggestContent}
    </Callout>
  ) : (
    <div className={autoSuggestListClassNames.supAutoSuggestContainer}>{autoSuggestContent}</div>
  );
};

export const SelfHelpAutoSuggest = styled(SelfHelpAutoSuggestC, getAutoSuggestListStyles);
